import { PureComponent } from 'react';
import PropTypes, { bool } from 'prop-types';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';

import { api, loadClientDetails } from '~/api';
import { triggerErrorAnalytics } from '~/analytics/analytics.listener';

import { AnalyticsQuestionsSection } from '~/components/analytics-questions-section';
import {
  BookingContext,
  errorMessagesActions,
  TermsAndConditionsNotice,
  TERMS_CONDITIONS_CONFIG,
} from '~/components';
import { NextButton } from '~/components/screen-nav/NextButton';
import { YOUR_DETAILS_FIELDS_REVIEW_CONFIG } from '~/components/review-details/your-details/you-details-fields-review.config';

import styles from './ReviewForm.scss';

// Custom validation function to replace jl-design-system validate
const validateField = (value, field) => {
  if (!field.validate) return undefined;

  for (const validator of field.validate) {
    // Ensure validator is a function before calling it
    if (typeof validator === 'function') {
      const error = validator(value);
      if (error) return error;
    }
  }
  return undefined;
};

const validateFields = (values, fields) => {
  const errors = {};

  fields.forEach((field) => {
    const error = validateField(values?.[field.name], field);
    if (error) {
      errors[field.name] = error;
    }
  });

  return Object.keys(errors).length > 0 ? errors : undefined;
};

export class ReviewForm extends PureComponent {
  lastHandledSubmitCount = -1;

  validateForm = (values) => {
    const { yourDetailsSection, termsConditions } = values;

    const errors = {};

    // Validate your details section
    const yourDetailsErrors = validateFields(yourDetailsSection, YOUR_DETAILS_FIELDS_REVIEW_CONFIG);
    if (yourDetailsErrors) {
      errors.yourDetailsSection = yourDetailsErrors;
    }

    // Validate terms and conditions
    const termsConditionsErrors = validateFields(termsConditions, TERMS_CONDITIONS_CONFIG);
    if (termsConditionsErrors) {
      errors.termsConditions = termsConditionsErrors;
    }

    return errors;
  };

  handleSubmitFail = (errors) => {
    const { dispatchPushSectionErrorsToErrorMessages, pathname, shopId } = this.props;

    if (errors?.termsConditions?.termsConditions) {
      triggerErrorAnalytics(
        `${api.BASENAME}${pathname}`,
        shopId,
        errors.termsConditions.termsConditions,
      );
    }

    dispatchPushSectionErrorsToErrorMessages();
  };

  render() {
    const {
      onSubmit = () => {},
      isRecommendedByAFriend = false,
      isAjaxCompleted,
      hideConfirmButton = false,
      initialValues,
    } = this.props;

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={this.validateForm}
        keepDirtyOnReinitialize
        render={({ handleSubmit, submitFailed, errors, submitCount }) => {
          // Only call handleSubmitFail once per unique submission attempt
          if (submitFailed && errors && submitCount > this.lastHandledSubmitCount) {
            this.lastHandledSubmitCount = submitCount;
            this.handleSubmitFail(errors);
          }

          return (
            <section
              data-testid="ReviewForm"
              className={styles.ReviewForm}
              data-test-ajax={isAjaxCompleted}
            >
              {isRecommendedByAFriend ? null : <AnalyticsQuestionsSection />}
              <form
                onSubmit={handleSubmit}
                data-testid="ReviewForm"
                noValidate
                className={ReviewForm}
                id="ReviewForm"
              >
                <TermsAndConditionsNotice />
              </form>
              {hideConfirmButton ? null : (
                <div className={styles.nextButton}>
                  <NextButton type="submit" text="Confirm booking" form="ReviewForm" />
                </div>
              )}
            </section>
          );
        }}
      />
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    appointment: {
      draft: {
        shop: { id: shopId },
      },
    },
    messages: {
      app: { errorMessages },
    },
    router: { location: { pathname } = {} } = {},
  } = state;

  return {
    shopId,
    errorMessages,
    pathname,
  };
};

const mapDispatchToProps = {
  dispatchPushSectionError: errorMessagesActions.pushSectionError,
  dispatchClearSectionErrors: errorMessagesActions.clearSectionErrorsAndErrorMessages,
  dispatchPushSectionErrorsToErrorMessages: errorMessagesActions.pushSectionErrorToErrorMessage,
  dispatchLoadClientDetails: loadClientDetails,
};

const ConnectedReviewForm = connect(mapStateToProps, mapDispatchToProps)(ReviewForm);

ReviewForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isRecommendedByAFriend: bool.isRequired,
  isAjaxCompleted: bool.isRequired,
  initialValues: PropTypes.object,
  pathname: PropTypes.string,
  shopId: PropTypes.string,
  dispatchPushSectionErrorsToErrorMessages: PropTypes.func.isRequired,
};

ReviewForm.contextType = BookingContext;

export default ConnectedReviewForm;
